import { useState, useEffect, useCallback, useRef } from "react";
import { api, apiErr } from "@/lib/api";
import { fmtTime } from "@/lib/format";
import { toast } from "sonner";
import { Server, ShieldAlert, RotateCw, RefreshCw, Check, X, Loader2 } from "lucide-react";

// Étapes affichées (backend BACKEND_STEPS + DEVICE_STEPS). Aucune n'est calculée côté client.
const JOB_STEPS = [
  "PRECHECK", "RELEASE_RESOLVE", "VERIFY", "LOCK", "DISPATCH",
  "FETCH", "VERIFY_MANIFEST", "VERIFY_FLEET_STATE", "BACKUP", "INSTALL",
  "COMPILE", "RESTART", "HEALTH", "WRITE_RELEASE", "UPDATE_INDEX",
];

const TERMINAL = ["success", "failed", "rolled_back", "rollback_failed", "result_unknown"];

// Libellés des contrôles préflight, dans l'ordre demandé.
const CHECK_LABELS = {
  infra_api: "Infra API",
  permissions: "Permissions",
  gateway: "Gateway",
  redis: "Redis",
  drone_connected: "DRONE-V1 connecté",
  drone_disarmed: "Aucune preuve d'armement",
  drone_landed: "Aucune preuve de vol",
  offboard_inactive: "Offboard inactif",
  no_active_command: "Aucune commande active",
  no_ota_job: "Aucun job OTA actif",
};
const CHECK_ORDER = Object.keys(CHECK_LABELS);

function CheckRow({ ok, label, testid }) {
  return (
    <div data-testid={testid} data-ok={ok ? "true" : "false"}
      className="flex items-center gap-2 font-mono text-[11px] py-1">
      {ok
        ? <Check size={13} className="text-[#22C55E] shrink-0" />
        : <X size={13} className="text-[#EF4444] shrink-0" />}
      <span className={ok ? "text-[#D4D4D4]" : "text-[#EF4444]"}>{label}</span>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0F0F0F] border border-white/10 rounded-sm p-3">
      <div className="text-[9px] uppercase text-[#737373] tracking-wider mb-1">{label}</div>
      <div className="font-mono text-[11px] text-white break-all">{value ?? "—"}</div>
    </div>
  );
}

export default function GatewayOtaTab() {
  const [pf, setPf] = useState(null);
  const [pfErr, setPfErr] = useState(null);
  const [releases, setReleases] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);   // { kind, current, target }
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [p, r, h] = await Promise.all([
      api.get("/gateway-ota/preflight").then((x) => x.data).catch((e) => ({ __err: apiErr(e) })),
      api.get("/gateway-ota/releases").then((x) => x.data).catch(() => ({ releases: [] })),
      api.get("/gateway-ota/jobs", { params: { limit: 20 } }).then((x) => x.data).catch(() => ({ jobs: [] })),
    ]);
    if (p.__err) { setPfErr(p.__err); setPf(null); } else { setPf(p); setPfErr(null); }
    setReleases(r.releases || []);
    setHistory(h.jobs || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Polling du job en cours. AUCUN second POST automatique (RESULT_UNKNOWN inclus).
  const pollJob = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/gateway-ota/jobs/${jobId}`);
        setJob(data);
        if (TERMINAL.includes(data.status)) {
          clearInterval(pollRef.current); pollRef.current = null;
          loadAll();
          if (data.status === "success") toast.success("Déploiement Gateway réussi");
          else if (data.status === "result_unknown") toast.warning("Résultat incertain — aucun redéploiement automatique");
          else toast.error(`Job Gateway terminé : ${data.status}`);
        }
      } catch { /* on continue de poller */ }
    }, 2000);
  }, [loadAll]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const runMutation = async () => {
    if (!confirm) return;
    setBusy(true);
    const kind = confirm.kind;
    const idemKey = `gw-${kind}-${Date.now()}`;
    try {
      const url = kind === "update" ? "/gateway-ota/deploy" : "/gateway-ota/rollback";
      const body = kind === "update" ? { release: confirm.target } : {};
      const { data } = await api.post(url, body, { headers: { "X-OTA-Key": idemKey } });
      setConfirm(null);
      setJob({ id: data.job_id, status: data.status || "queued", steps: [] });
      pollJob(data.job_id);
      toast.info("Job Gateway lancé");
    } catch (e) {
      toast.error(apiErr(e, "Lancement du job Gateway impossible"));
    } finally {
      setBusy(false);
    }
  };

  const readyUpdate = pf?.ready_update === true;      // dérivé UNIQUEMENT du backend
  const readyRollback = pf?.ready_rollback === true;
  const checksByKey = Object.fromEntries((pf?.checks || []).map((c) => [c.key, c.ok]));
  const jobActive = job && !TERMINAL.includes(job.status);

  return (
    <div className="space-y-4 max-w-3xl" data-testid="gateway-ota-panel">
      {/* Carte principale */}
      <div className="bg-[#141414] border border-white/10 rounded-sm p-5 space-y-4" data-testid="gateway-ota-card">
        <div className="flex items-center gap-2">
          <Server size={16} className="text-[#A855F7]" />
          <span className="font-chivo font-black uppercase tracking-tight">SkyCore Gateway — Déploiement OTA</span>
          <button data-testid="gateway-ota-refresh" onClick={loadAll}
            className="ml-auto flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-2 py-1 hover:bg-white/10">
            <RefreshCw size={11} /> Rafraîchir
          </button>
        </div>

        {/* Impact flotte */}
        <div data-testid="gateway-ota-warning" className="flex items-start gap-2 bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-sm px-4 py-3">
          <ShieldAlert size={16} className="text-[#EF4444] shrink-0 mt-0.5" />
          <div className="font-mono text-[11px] text-[#EF4444]">
            <span className="font-bold uppercase mr-2 border border-[#EF4444]/50 rounded px-1">Impact flotte</span>
            Une opération OTA sur la Gateway centrale redémarre le service et peut affecter <b>toute la flotte</b> (déconnexion/reconnexion temporaire des drones).
          </div>
        </div>

        {pfErr && (
          <div data-testid="gateway-ota-pf-error" className="font-mono text-[11px] text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-sm px-3 py-2">
            Préflight indisponible : {pfErr}. Boutons désactivés (fail-closed).
          </div>
        )}

        {/* PRE-FLIGHT OTA */}
        <div data-testid="gateway-ota-preflight" className="bg-[#0F0F0F] border border-white/10 rounded-sm p-3">
          <div className="text-[9px] uppercase text-[#737373] tracking-wider mb-2 flex items-center gap-2">
            Pré-flight OTA
            {loading && <Loader2 size={11} className="animate-spin text-[#737373]" />}
            {pf?.checked_at && <span className="ml-auto normal-case tracking-normal" data-testid="gateway-ota-checked-at">Vérifié : {fmtTime(pf.checked_at)}</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            {CHECK_ORDER.map((k) => (
              <CheckRow key={k} ok={!!checksByKey[k]} label={CHECK_LABELS[k]} testid={`gateway-ota-check-${k}`} />
            ))}
          </div>
        </div>

        {/* Release / commit / job actif */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Release installée" value={pf?.installed_release} testid="gateway-ota-installed" />
          <Field label="Release approuvée" value={pf?.available_release} testid="gateway-ota-available" />
          <Field label="Commit (git_sha)" value={pf?.available_git_sha ? pf.available_git_sha.slice(0, 12) : null} testid="gateway-ota-commit" />
          <Field label="Job actif" value={pf?.active_job ? `${pf.active_job.id} (${pf.active_job.status})` : "Aucun"} testid="gateway-ota-active-job" />
        </div>

        {/* Actions — état dérivé UNIQUEMENT des drapeaux backend ready_* */}
        <div className="flex flex-wrap gap-2 items-center">
          <button data-testid="gateway-ota-update" disabled={!readyUpdate || busy || jobActive}
            onClick={() => setConfirm({ kind: "update", current: pf?.installed_release, target: pf?.available_release })}
            title={readyUpdate ? "" : "Préflight non validé (fail-closed)"}
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider bg-[#A855F7] rounded-sm px-3 py-2 hover:bg-[#A855F7]/85 disabled:opacity-40 disabled:cursor-not-allowed">
            <RotateCw size={12} /> Mettre à jour la Gateway
          </button>
          <button data-testid="gateway-ota-rollback" disabled={!readyRollback || busy || jobActive}
            onClick={() => setConfirm({ kind: "rollback", current: pf?.installed_release, target: pf?.previous_known_good })}
            title={readyRollback ? "" : "Rollback indisponible (fail-closed)"}
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider border border-[#F59E0B]/40 text-[#F59E0B] rounded-sm px-3 py-2 hover:bg-[#F59E0B]/10 disabled:opacity-40 disabled:cursor-not-allowed">
            <RotateCw size={12} /> Rollback Gateway
          </button>
          {!readyUpdate && !readyRollback && !loading && (
            <span data-testid="gateway-ota-disabled-hint" className="font-mono text-[10px] text-[#525252] self-center">
              Actions verrouillées par le préflight backend (fail-closed).
            </span>
          )}
        </div>
      </div>

      {/* Progression du job en cours */}
      {job && (
        <div className="bg-[#141414] border border-white/10 rounded-sm p-4" data-testid="gateway-ota-job">
          <div className="text-[9px] uppercase text-[#737373] tracking-wider mb-2 flex items-center gap-2">
            Job {job.id} — <span data-testid="gateway-ota-job-status" className="text-white">{job.status}</span>
            {jobActive && <Loader2 size={11} className="animate-spin text-[#A855F7]" />}
          </div>
          {job.status === "result_unknown" && (
            <div data-testid="gateway-ota-result-unknown" className="font-mono text-[11px] text-[#F59E0B] mb-2">
              Résultat incertain — état Gateway non confirmé. Aucun redéploiement automatique ; vérifier manuellement.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            {JOB_STEPS.map((name) => {
              const s = (job.steps || []).find((x) => x.name === name);
              const st = s?.status || "pending";
              const col = st === "success" ? "#22C55E" : st === "failed" ? "#EF4444"
                : st === "running" ? "#A855F7" : st === "skipped" ? "#525252" : "#3F3F3F";
              return (
                <div key={name} data-testid={`gateway-ota-step-${name}`} className="flex items-center gap-2 font-mono text-[10px] py-0.5">
                  <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: col }} />
                  <span className="text-[#A3A3A3]">{name}</span>
                  <span className="ml-auto uppercase" style={{ color: col }}>{st}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Historique */}
      <div className="bg-[#141414] border border-white/10 rounded-sm p-4" data-testid="gateway-ota-history">
        <div className="text-[9px] uppercase text-[#737373] tracking-wider mb-2">Historique déploiements Gateway</div>
        {history.length === 0 ? (
          <div className="font-mono text-[11px] text-[#525252]">Aucun déploiement Gateway.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left font-mono text-[10px]">
              <thead className="text-[#737373] uppercase tracking-wider">
                <tr>
                  <th className="py-1 pr-3">Date</th><th className="pr-3">Type</th><th className="pr-3">Release</th>
                  <th className="pr-3">git_sha</th><th className="pr-3">Précédente</th><th className="pr-3">Statut</th><th>Par</th>
                </tr>
              </thead>
              <tbody>
                {history.map((j) => (
                  <tr key={j.id} data-testid={`gateway-ota-history-row-${j.id}`} className="border-t border-white/5">
                    <td className="py-1 pr-3 text-[#A3A3A3]">{fmtTime(j.created_at)}</td>
                    <td className="pr-3">{j.kind}</td>
                    <td className="pr-3 text-white">{j.release || "—"}</td>
                    <td className="pr-3 text-[#737373]">{j.git_sha ? j.git_sha.slice(0, 10) : "—"}</td>
                    <td className="pr-3 text-[#737373]">{j.previous_release || "—"}</td>
                    <td className="pr-3">
                      {j.status === "result_unknown"
                        ? <span data-testid={`gateway-ota-history-unknown-${j.id}`} className="text-[#F59E0B]">RESULT_UNKNOWN</span>
                        : <span className={j.status === "success" ? "text-[#22C55E]" : ["failed", "rollback_failed"].includes(j.status) ? "text-[#EF4444]" : "text-[#A3A3A3]"}>{j.status}</span>}
                    </td>
                    <td className="text-[#737373]">{j.created_by_email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation riche */}
      {confirm && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 p-4" data-testid="gateway-ota-confirm">
          <div className="bg-[#141414] border border-white/15 rounded-sm p-5 max-w-md w-full space-y-3">
            <div className="flex items-center gap-2 font-chivo font-black uppercase tracking-tight">
              <ShieldAlert size={16} className="text-[#EF4444]" />
              {confirm.kind === "update" ? "Confirmer la mise à jour Gateway" : "Confirmer le rollback Gateway"}
            </div>
            <div className="font-mono text-[11px] text-[#D4D4D4] space-y-1.5">
              <div>Release actuelle : <b className="text-white" data-testid="gateway-ota-confirm-current">{confirm.current || "—"}</b></div>
              <div>
                {confirm.kind === "update" ? "Release cible : " : "Retour vers (previous_known_good) : "}
                <b className="text-white" data-testid="gateway-ota-confirm-target">{confirm.target || "—"}</b>
              </div>
              <ul className="list-disc list-inside text-[#F59E0B] pt-1 space-y-0.5">
                <li>Impact sur <b>toute la flotte</b> (tous les drones connectés)</li>
                <li>Redémarrage du service Gateway</li>
                <li>Déconnexion / reconnexion temporaire des drones attendue</li>
              </ul>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button data-testid="gateway-ota-confirm-cancel" onClick={() => setConfirm(null)} disabled={busy}
                className="font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10 disabled:opacity-40">Annuler</button>
              <button data-testid="gateway-ota-confirm-yes" onClick={runMutation} disabled={busy}
                className="font-mono text-[10px] uppercase tracking-wider bg-[#EF4444] hover:bg-[#EF4444]/85 rounded-sm px-4 py-2 disabled:opacity-50 flex items-center gap-1.5">
                {busy && <Loader2 size={11} className="animate-spin" />} Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
